/**
 * Topbar pokes dropdown.
 *
 * Mirrors the legacy CRA `Layout1Topbar` pokes panel — an icon button next to
 * the bell with a count badge; clicking it pops a panel listing pokes the
 * signed-in user has received, each with a "Poke back" + dismiss button.
 *
 * Pokes used to live in the `/dashboard/notifications` right rail; moved here
 * 2026-05-31 so the user gets at-a-glance access from any page.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hand, Loader2, X } from 'lucide-react';

import { Button, UserAvatar } from '@/components/ui';
import { useToast } from '@/components/ui/use-toast';
import { ApiError } from '@/lib/api';
import { useCounts } from '@/lib/api/hooks/useCounts';
import {
  usePokes,
  usePokeMutation,
  useCancelPokeMutation,
  type PokeItem,
} from '@/pages/notifications/hooks/usePokes';
import { fullName, initials } from '@/pages/profile/lib/profile';

import { DotBadge } from './_shared';

interface PokeRowProps {
  item: PokeItem;
  onPokeBack: (id: number) => void;
  onCancel: (id: number) => void;
  pokingId: number | null;
  cancelingId: number | null;
  onClose: () => void;
}

function PokeRow({
  item,
  onPokeBack,
  onCancel,
  pokingId,
  cancelingId,
  onClose,
}: PokeRowProps) {
  const name = fullName(item) || item.username || 'Someone';
  const isPoking = pokingId === item.ID;
  const isCanceling = cancelingId === item.ID;

  return (
    <div className="flex items-start gap-3 px-3 py-3">
      <Link
        to={item.username ? `/user/${item.username}` : '#'}
        onClick={onClose}
      >
        <UserAvatar
          src={item.image}
          alt={name}
          fallback={initials(item)}
          plan={item.plan}
          className="h-10 w-10 shrink-0"
        />
      </Link>
      <div className="min-w-0 flex-1">
        <Link
          to={item.username ? `/user/${item.username}` : '#'}
          onClick={onClose}
          className="block truncate text-sm font-medium text-ink hover:underline"
        >
          {name}
        </Link>
        <p className="text-xs text-muted-foreground">poked you</p>
        <div className="mt-2 flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => onPokeBack(item.ID)}
            disabled={isPoking || isCanceling}
            aria-label={`Poke back ${name}`}
          >
            {isPoking ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Hand className="h-4 w-4" />
            )}
            Poke back
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onCancel(item.ID)}
            disabled={isPoking || isCanceling}
            aria-label={`Dismiss poke from ${name}`}
          >
            {isCanceling ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <X className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function TopbarPokes() {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { toast } = useToast();
  const { data: counts } = useCounts();
  const { data, isLoading } = usePokes();
  const pokeBack = usePokeMutation();
  const cancelPoke = useCancelPokeMutation();

  const items = data?.list ?? [];

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  function handlePokeBack(id: number) {
    pokeBack.mutate(id, {
      onSuccess: () => toast({ variant: 'success', description: 'Poke sent.' }),
      onError: (err) =>
        toast({
          variant: 'destructive',
          description: err instanceof ApiError ? err.message : 'Could not send poke.',
        }),
    });
  }

  function handleCancel(id: number) {
    cancelPoke.mutate(id, {
      onError: (err) =>
        toast({
          variant: 'destructive',
          description: err instanceof ApiError ? err.message : 'Could not dismiss poke.',
        }),
    });
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Pokes"
        aria-haspopup="dialog"
        aria-expanded={open}
        className="relative text-white hover:bg-white/15 hover:text-white"
        onClick={() => setOpen((s) => !s)}
      >
        <Hand className="h-5 w-5" />
        <DotBadge count={counts?.pokes} />
      </Button>

      {open ? (
        <div
          role="dialog"
          aria-label="Pokes"
          className="absolute right-0 top-full z-30 mt-2 w-80 max-w-[90vw] overflow-hidden rounded-md border border-border bg-surface text-ink shadow-lg"
        >
          <div className="flex items-center gap-2 border-b border-border px-4 py-3">
            <Hand className="h-4 w-4 text-brand-700" />
            <h3 className="font-display text-sm font-medium">Pokes</h3>
          </div>

          {isLoading ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              Loading pokes…
            </p>
          ) : items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              No pokes yet.
            </p>
          ) : (
            <div className="max-h-[60vh] divide-y divide-border overflow-y-auto">
              {items.map((item) => (
                <PokeRow
                  key={item.ID}
                  item={item}
                  onPokeBack={handlePokeBack}
                  onCancel={handleCancel}
                  pokingId={
                    pokeBack.isPending ? (pokeBack.variables ?? null) : null
                  }
                  cancelingId={
                    cancelPoke.isPending ? (cancelPoke.variables ?? null) : null
                  }
                  onClose={() => setOpen(false)}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
